/**
 * Self-hosted app updates.
 *
 * The server publishes /downloads/latest.json on every `publish-apk.sh`. The
 * app compares its own versionCode against it and offers the update in place,
 * so you never have to remember to check a URL.
 *
 * On web this is inert: the browser always has the latest build, because it
 * fetches it. Every function here returns "nothing to do" off-platform.
 */

import { registerPlugin } from '@capacitor/core'

import { withTimeout, withTimeoutOr } from '../lib/timeout'
import { isNative } from './platform'

// Injected by Vite at build time — see vite.config.ts and deploy/build-apk.sh.
declare const __BUILD_VERSION_CODE__: number
declare const __BUILD_VERSION_NAME__: string

const BUILD_VERSION_CODE =
  typeof __BUILD_VERSION_CODE__ === 'number' ? __BUILD_VERSION_CODE__ : 0
const BUILD_VERSION_NAME =
  typeof __BUILD_VERSION_NAME__ === 'string' ? __BUILD_VERSION_NAME__ : 'dev'

export type LatestInfo = {
  versionCode: number
  versionName: string
  url: string
  sha256: string
  sizeBytes: number
  /** What changed. Shown in the banner so an update isn't just a number. */
  notes?: string | null
  publishedAt: string
}

export type UpdateStatus = {
  available: boolean
  current?: string
  latest?: LatestInfo
  /** False when Android hasn't been granted permission to install packages. */
  canInstall?: boolean
}

/** What the native side did with a download once it finished. */
export type InstallOutcome = {
  /** Android's installer dialog was actually launched. */
  launched: boolean
  /** The grant went missing between the check and the launch — go to Settings. */
  needsPermission: boolean
}

type InstallerPlugin = {
  canInstall(): Promise<{ allowed: boolean }>
  openInstallSettings(): Promise<{ opened: boolean }>
  downloadAndInstall(options: { url: string; sha256?: string }): Promise<InstallOutcome>
  addListener(
    event: 'progress',
    fn: (data: { percent: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>
}

const Installer = registerPlugin<InstallerPlugin>('Installer')

/**
 * The base to fetch latest.json from.
 *
 * VITE_API_BASE is the absolute origin in the APK build and empty on web
 * (same-origin). Same value the API client uses, so there is one place that
 * knows where the server lives.
 */
const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

/**
 * Fetch the published build info.
 *
 * Goes through /api/v1/version, NOT /downloads/latest.json.
 *
 * That distinction is the whole bug this function exists to avoid: the app runs
 * on origin https://localhost, so a fetch to macro.npcforge.ca is cross-origin.
 * /downloads/ is served by nginx as static files with no CORS header, so the
 * browser blocks the request — and a blocked fetch throws, gets caught, and is
 * indistinguishable from "there is no update". The banner silently never
 * appeared. /api/v1/* already carries the right allow-origin for the WebView,
 * so this works by construction.
 *
 * Falls back to the static file for the web build, where same-origin makes it
 * a non-issue and it saves a round trip through Flask.
 */
async function fetchLatest(): Promise<LatestInfo | null> {
  try {
    const res = await withTimeout(
      fetch(`${BASE}/api/v1/version`, { cache: 'no-store' }),
      8000,
      'GET /api/v1/version',
    )
    if (res.ok) {
      const body = (await res.json()) as { latest: LatestInfo | null }
      if (body?.latest) return body.latest
      return null
    }
  } catch {
    /* older server without /version, or offline — try the static path */
  }

  try {
    const res = await withTimeout(
      fetch(`${BASE}/downloads/latest.json`, { cache: 'no-store' }),
      8000,
      'GET /downloads/latest.json',
    )
    if (!res.ok) return null
    return (await res.json()) as LatestInfo
  } catch {
    // Offline, or the server is unreachable. Not worth surfacing — checking
    // for updates is never why someone opened the app.
    return null
  }
}

export async function checkForUpdate(): Promise<UpdateStatus> {
  if (!isNative()) return { available: false }

  // The installed versionCode is baked in at BUILD time by deploy/build-apk.sh,
  // which passes the same number to Vite and to Gradle.
  //
  // It used to come from @capacitor/app's getInfo(). That call hung on a real
  // device — the server access log showed the phone happily making other API
  // calls while /api/v1/version was never requested once, so the check was
  // stuck before it ever reached the network. Reading a build-time constant
  // needs no native bridge at all, so there is nothing left to hang.
  const currentCode = BUILD_VERSION_CODE
  const currentName = BUILD_VERSION_NAME

  const latest = await fetchLatest()
  if (!latest) return { available: false, current: currentName }

  if (!latest.versionCode || latest.versionCode <= currentCode) {
    return { available: false, current: currentName }
  }

  // Bounded: if the plugin is missing or wedged, assume we can't install and
  // show the "Allow installs" path rather than hanging forever.
  const canInstall = await withTimeoutOr(
    Installer.canInstall().then((r) => r.allowed),
    4000,
    false,
    'Installer.canInstall',
  )

  return { available: true, current: currentName, latest, canInstall }
}

/**
 * Ask Android, right now, whether we may install packages.
 *
 * Called fresh every time it matters rather than cached, because the answer
 * changes the moment the user flips the toggle in Settings — and a cached
 * "no" is what made the update button bounce back to Settings forever.
 */
export async function canInstallNow(): Promise<boolean> {
  if (!isNative()) return false
  return withTimeoutOr(
    Installer.canInstall().then((r) => r.allowed),
    4000,
    false,
    'Installer.canInstall',
  )
}

/**
 * Returns whether a settings screen actually opened. A silent false is the
 * difference between "go and flip the toggle" and "nothing happened when I
 * tapped it", and the UI needs to be able to tell you which.
 */
export async function openInstallSettings(): Promise<boolean> {
  try {
    const r = await Installer.openInstallSettings()
    return r?.opened !== false
  } catch {
    return false   // not on Android, or no settings screen resolved
  }
}

export async function installUpdate(
  latest: LatestInfo,
  onProgress?: (percent: number) => void,
): Promise<InstallOutcome> {
  let listener: { remove: () => Promise<void> } | null = null
  if (onProgress) {
    try {
      listener = await Installer.addListener('progress', (d) => onProgress(d.percent))
    } catch {
      /* progress is a nicety; carry on without it */
    }
  }

  try {
    // Resolves once Android's install dialog has been launched. The user still
    // confirms it — that's mandatory for any sideloaded package and not
    // something to design around.
    const outcome = await Installer.downloadAndInstall({
      url: latest.url,
      sha256: latest.sha256,
    })
    // An older native build resolves with nothing; treat that as launched, which
    // is what it did.
    return {
      launched: outcome?.launched !== false,
      needsPermission: outcome?.needsPermission === true,
    }
  } finally {
    await listener?.remove().catch(() => {})
  }
}
